import { create } from 'zustand';

import { applyTheme, getTheme, toggleTheme as flipTheme } from '@/theme';
import { setSoundEnabled } from '@/lib/notify';

/**
 * Настройки интерфейса: тема, звук, свёрнутость панелей, глобальный поиск.
 *
 * Всё, что оператор выбрал руками, переживает перезагрузку. Ключи заведены
 * поштучно, а не одним JSON-блобом: сломанный блоб обнуляет все настройки
 * разом, а тут потеря ограничена одной.
 */

const KEY = {
  sound: 'support_sound',
  folders: 'support_folders',
  info: 'support_info',
} as const;

function readFlag(key: string, fallback: boolean): boolean {
  const raw = localStorage.getItem(key);
  if (raw === '1') return true;
  if (raw === '0') return false;
  return fallback;
}

function writeFlag(key: string, value: boolean): void {
  localStorage.setItem(key, value ? '1' : '0');
}

export type Theme = 'light' | 'dark';

export interface UiState {
  theme: Theme;
  /** Звук уведомлений. Выключается в меню оператора и запоминается. */
  soundEnabled: boolean;
  /** Колонка папок (Все / Новые / Непрочитанные / Архив). */
  foldersVisible: boolean;
  /** Правая панель «Информация». */
  infoOpen: boolean;
  /** Глобальный поиск (Cmd/Ctrl+K). Открывает его каркас, рисует чужой компонент. */
  searchOpen: boolean;

  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setSound: (enabled: boolean) => void;
  toggleSound: () => void;
  toggleFolders: () => void;
  setInfoOpen: (open: boolean) => void;
  toggleInfo: () => void;
  setSearchOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  // theme.ts — единственный владелец data-theme и localStorage темы; стор
  // только зеркалит его значение, чтобы компоненты перерисовывались.
  theme: getTheme(),
  soundEnabled: readFlag(KEY.sound, true),
  foldersVisible: readFlag(KEY.folders, true),
  infoOpen: readFlag(KEY.info, false),
  searchOpen: false,

  toggleTheme: () => set({ theme: flipTheme() }),

  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },

  setSound: (enabled) => {
    writeFlag(KEY.sound, enabled);
    // Модуль уведомлений живёт вне React и читает флаг синхронно на каждом
    // входящем: подписывать его на стор было бы дороже, чем протолкнуть значение.
    setSoundEnabled(enabled);
    set({ soundEnabled: enabled });
  },

  toggleSound: () => get().setSound(!get().soundEnabled),

  toggleFolders: () => {
    const next = !get().foldersVisible;
    writeFlag(KEY.folders, next);
    set({ foldersVisible: next });
  },

  setInfoOpen: (open) => {
    writeFlag(KEY.info, open);
    set({ infoOpen: open });
  },

  toggleInfo: () => get().setInfoOpen(!get().infoOpen),

  setSearchOpen: (open) => set({ searchOpen: open }),
}));

/**
 * Проставить сохранённые настройки до первого рендера.
 *
 * Тему применяет main.tsx ещё раньше — иначе первый кадр приходит светлым и
 * мигает. Здесь остаётся звук, который важен только к первому событию.
 */
export function initUi(): void {
  setSoundEnabled(useUiStore.getState().soundEnabled);
}
